import {
  Component,
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  JsonForms,
  JsonFormsDispatch,
  withJsonFormsControlProps,
  withJsonFormsLayoutProps,
} from '@jsonforms/react';
import {
  rankWith,
  and,
  or,
  uiTypeIs,
  isControl,
  isBooleanControl,
  isEnumControl,
  isNumberControl,
  isIntegerControl,
  isStringControl,
  isObjectControl,
  isObjectArrayControl,
  isPrimitiveArrayControl,
  schemaMatches,
  schemaSubPathMatches,
  type ControlProps,
  type LayoutProps,
  type JsonSchema,
  type Layout,
  type JsonFormsRendererRegistryEntry,
} from '@jsonforms/core';
import { toast } from 'sonner';
import { Info, PanelRight, PanelRightClose, X, ZoomIn, ZoomOut, RotateCcw, Maximize2, Minimize2 } from 'lucide-react';

import { VisualizationTreeGraphCanvas } from './VisualizationTreeGraphCanvas';
import { VisualizationArrayGraphCanvas } from './VisualizationArrayGraphCanvas';
import { VisualizationGridCanvas } from './VisualizationGridCanvas';
import { StateKeyValueEditor } from './StateKeyValueEditor';
import { VisualizationStageEditor } from './VisualizationStageEditor';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  trainerContentApi,
  type TopicVisualization,
  type UpsertVisualizationRequest,
} from '@/lib/api/trainer';
import vizSchema from '@/features/Visualizer/schema.generated.json';
import { Player } from '@/features/Visualizer/Player';
import {
  buildTimeline,
  type StepScript,
  type Variant,
  type Step,
  type TreeView,
  type GraphView,
  type ArrayView,
  type GridView,
  type StackView,
  type TopicVisualization as PlayerVisualization,
} from '@/features/Visualizer/types';

interface Props {
  batchId: string;
  topicId: string;
  hasSourceTopic: boolean;
  /**
   * Invoked once it's safe to close the (typically full-screen) surface this
   * editor is embedded in — after `requestClose` (exposed via `ref`) has
   * confirmed there's no unsaved draft, or the user explicitly chose to
   * discard it. Not called directly by this component's own UI.
   */
  onRequestClose?: () => void;
}

/** Imperative handle so a full-screen host (TopicContentEditor) can route
 * Escape/close-button dismissal through the same unsaved-changes guard this
 * component's own close affordances use, instead of closing out from under
 * an in-progress edit. */
export interface VisualizationEditorHandle {
  requestClose: () => void;
}

const emptyDraft = { kind: 'concept', schema_version: 1, script: { variants: [] } };

/**
 * The live preview (`Player`, right column) and the trainer's editing point
 * (`VisualizationStageEditor`, left column) previously had no connection to
 * each other beyond sharing the same `draft` — `Player`'s own `usePlayer`
 * defaults to the FIRST step of the FIRST variant regardless of which step
 * the trainer is actually editing. Editing step 4 while the preview sits on
 * step 1 makes every edit look like it "isn't reflecting" (it is — just on a
 * step that isn't visible). `ObjectArrayControl` (a JSON Forms renderer,
 * with no direct access to this component's own state) reports the
 * variant+step the trainer is currently viewing through this context;
 * `VisualizationEditor` seeds `Player` with it so the preview always follows
 * the edit point.
 */
const FocusStepContext = createContext<(variantIndex: number, stepIndex: number) => void>(() => {});

/** Same `document.fullscreenElement` pattern as `Player`'s and `TopicContentEditor`'s own fullscreen toggles — here scoped to the live-preview panel, so a trainer checking a diagram/tree in detail isn't limited to the panel's ~640px width. */
function useFullscreen(ref: React.RefObject<HTMLElement | null>) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    const handler = () => setIsFullscreen(document.fullscreenElement === ref.current);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, [ref]);
  const toggle = () => {
    if (document.fullscreenElement === ref.current) document.exitFullscreen();
    else ref.current?.requestFullscreen();
  };
  return { isFullscreen, toggle };
}

/**
 * `ts-json-schema-generator` emits a root schema shaped as `{ $ref, definitions }`
 * rather than inlining `TopicVisualization`'s own `properties` at the top level.
 * Unwrap that indirection once so scope-based Control resolution (`#/properties/x`)
 * works against the root schema directly, while keeping `definitions` around for
 * the nested `$ref`s (Variant, Step, View, …) used throughout the tree.
 */
function resolveRootSchema(raw: unknown): JsonSchema {
  const schema = raw as { $ref?: string; definitions?: Record<string, JsonSchema> };
  if (!schema.$ref || !schema.definitions) return raw as JsonSchema;
  const match = /^#\/definitions\/(.+)$/.exec(schema.$ref);
  const target = match ? schema.definitions[match[1]] : undefined;
  if (!target) return raw as JsonSchema;
  return { ...target, definitions: schema.definitions } as JsonSchema;
}

const resolvedVizSchema = resolveRootSchema(vizSchema);

/**
 * Minimal Tailwind-styled JSON Forms renderer set.
 *
 * This codebase uses Tailwind + shadcn-style components everywhere else and does
 * not otherwise depend on MUI. @jsonforms/material-renderers would pull in
 * @mui/material, @mui/icons-material, @mui/x-date-pickers, @emotion/react and
 * @emotion/styled — a large, style-inconsistent addition — while JSON Forms'
 * core is renderer-agnostic (any {tester, renderer} pair works via
 * `withJsonFormsControlProps` / `withJsonFormsLayoutProps` /
 * `withJsonFormsArrayControlProps`). This set intentionally covers only the
 * scalar/array-of-primitive/array-of-object shapes this schema actually
 * exercises for the base editor; the deeply-nested `Step.views` (a
 * discriminated `oneOf` on `kind`) is left to a raw JSON fallback here — Task 8
 * owns the real per-view visual editor.
 */

function fieldLabel(props: ControlProps) {
  return props.label || props.path.split('/').pop() || '';
}

function schemaProperties(schema: JsonSchema): Record<string, JsonSchema> {
  return ((schema.properties as unknown as Record<string, JsonSchema>) ?? {}) as Record<
    string,
    JsonSchema
  >;
}

/** Builds a flat VerticalLayout of Controls, one per top-level property of a schema. */
function buildVerticalUischema(schema: JsonSchema): Layout {
  const properties = schemaProperties(schema);
  return {
    type: 'VerticalLayout',
    elements: Object.keys(properties).map((key) => ({
      type: 'Control',
      scope: `#/properties/${key}`,
    })),
  };
}

/**
 * These `TopicVisualization` fields are internal bookkeeping a trainer
 * building a visualization for the first time doesn't need to see:
 * `schema_version` already defaults to `1` and is essentially never
 * touched by hand, and `difficulty`/`leetcode_id`/`problem_slug` only apply
 * to `kind: 'problem'` visualizations, not the far more common `'concept'`
 * ones. Tucked behind an "Advanced" `AdvancedGroupRenderer` instead of
 * sitting inline before the actual content (Kind, Variants, Steps).
 */
const ADVANCED_ROOT_KEYS = ['difficulty', 'leetcode_id', 'problem_slug', 'schema_version'];

/**
 * `Variant.complexity_time`/`complexity_space` are the annotation a trainer
 * adds AFTER writing a variant's steps ("this approach is O(n log n)"), not
 * something they need staring at them while typing the Label/Note/
 * Pseudocode that actually define the variant. Same "Advanced" treatment.
 */
const ADVANCED_VARIANT_KEYS = ['complexity_time', 'complexity_space'];

/**
 * Like `buildVerticalUischema`, but pulls `advancedKeys` out into a
 * collapsed trailing Group (rendered by `AdvancedGroupRenderer`) instead of
 * listing every property inline in declaration order. Used for the editor's
 * root schema (`ADVANCED_ROOT_KEYS`) and, per-item, for the Variants array
 * (`ADVANCED_VARIANT_KEYS`) — see `ObjectArrayControl`'s non-`oneOf` branch.
 */
function buildGroupedUischema(schema: JsonSchema, advancedKeys: string[]): Layout {
  const properties = schemaProperties(schema);
  const mainKeys = Object.keys(properties).filter((key) => !advancedKeys.includes(key));
  const presentAdvancedKeys = advancedKeys.filter((key) => key in properties);
  const elements: Layout['elements'] = mainKeys.map((key) => ({
    type: 'Control',
    scope: `#/properties/${key}`,
  }));
  if (presentAdvancedKeys.length > 0) {
    elements.push({
      type: 'Group',
      label: 'Advanced',
      elements: presentAdvancedKeys.map((key) => ({ type: 'Control', scope: `#/properties/${key}` })),
    } as unknown as Layout);
  }
  return { type: 'VerticalLayout', elements };
}

function ControlWrapper({ children, props }: { children: React.ReactNode; props: ControlProps }) {
  if (!props.visible) return null;
  const errorText = props.errors ? props.errors : undefined;
  return (
    <div className="mb-2">
      <label className="mb-1 block text-xs font-medium text-[#374151]" htmlFor={props.id}>
        {fieldLabel(props)}
        {props.required && <span className="ml-0.5 text-red-500">*</span>}
      </label>
      {children}
      {errorText && <p className="mt-1 text-[11px] text-red-600">{errorText}</p>}
    </div>
  );
}

function StringControl(props: ControlProps) {
  return (
    <ControlWrapper props={props}>
      <input
        id={props.id}
        type="text"
        className="w-full rounded-md border border-[#d1d5db] px-2 py-1 text-sm"
        value={props.data ?? ''}
        disabled={!props.enabled}
        onChange={(e) => props.handleChange(props.path, e.target.value)}
      />
    </ControlWrapper>
  );
}

function NumberControl(props: ControlProps) {
  return (
    <ControlWrapper props={props}>
      <input
        id={props.id}
        type="number"
        className="w-full rounded-md border border-[#d1d5db] px-2 py-1 text-sm"
        value={props.data ?? ''}
        disabled={!props.enabled}
        onChange={(e) =>
          props.handleChange(props.path, e.target.value === '' ? undefined : Number(e.target.value))
        }
      />
    </ControlWrapper>
  );
}

function BooleanControl(props: ControlProps) {
  return (
    <ControlWrapper props={props}>
      <input
        id={props.id}
        type="checkbox"
        className="h-4 w-4 rounded border-[#d1d5db]"
        checked={!!props.data}
        disabled={!props.enabled}
        onChange={(e) => props.handleChange(props.path, e.target.checked)}
      />
    </ControlWrapper>
  );
}

function EnumControl(props: ControlProps) {
  const options: string[] = (props.schema.enum as string[]) ?? [];
  return (
    <ControlWrapper props={props}>
      <select
        id={props.id}
        className="w-full rounded-md border border-[#d1d5db] bg-white px-2 py-1 text-sm"
        value={props.data ?? ''}
        disabled={!props.enabled}
        onChange={(e) => props.handleChange(props.path, e.target.value === '' ? undefined : e.target.value)}
      >
        {/* Selectable (not `disabled`) so an optional enum, once set, can be
            cleared back to unset — a `disabled` placeholder can only ever be
            the initial value, never chosen again after the user picks
            something else. */}
        <option value="">Select…</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </ControlWrapper>
  );
}

/** String[] fields (e.g. `pseudocode`) editable as one line per array entry. */
function PrimitiveArrayControl(props: ControlProps) {
  const items: unknown[] = Array.isArray(props.data) ? props.data : [];
  if (!props.visible) return null;
  return (
    <div className="mb-3 rounded-md border border-[#e5e7eb] p-2">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-medium text-[#374151]">{fieldLabel(props)}</span>
        <button
          type="button"
          className="text-xs text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline"
          disabled={!props.enabled}
          onClick={() => props.handleChange(props.path, [...items, ''])}
        >
          + Add
        </button>
      </div>
      {items.map((item, index) => (
        <div key={index} className="mb-1 flex items-center gap-1">
          <input
            type="text"
            className="w-full rounded-md border border-[#d1d5db] px-2 py-1 text-sm"
            value={typeof item === 'string' ? item : String(item ?? '')}
            disabled={!props.enabled}
            onChange={(e) => {
              const next = [...items];
              next[index] = e.target.value;
              props.handleChange(props.path, next);
            }}
          />
          <button
            type="button"
            className="shrink-0 text-xs text-destructive hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline"
            disabled={!props.enabled}
            onClick={() => props.handleChange(props.path, items.filter((_, i) => i !== index))}
          >
            Remove
          </button>
        </div>
      ))}
    </div>
  );
}

/** Follows `$ref` pointers (one or more hops) against the shared `definitions` map. */
function dereference(schema: JsonSchema, rootSchema: JsonSchema): JsonSchema {
  let current: JsonSchema & { $ref?: string } = schema;
  let guard = 0;
  while (current?.$ref && guard < 10) {
    const match = /^#\/definitions\/(.+)$/.exec(current.$ref);
    const definitions = rootSchema.definitions as Record<string, JsonSchema> | undefined;
    const target = match ? definitions?.[match[1]] : undefined;
    if (!target) break;
    current = target;
    guard += 1;
  }
  return current ?? {};
}

/**
 * Resolves which `oneOf` branch a given array item belongs to, purely from its
 * own `kind` discriminator value — mirroring Task 6's verified mechanism
 * (`properties.kind.const` per branch), not the schema's informal top-level
 * `discriminator` block. Returns `undefined` if the item's `kind` doesn't
 * match any branch (e.g. a freshly-added empty `{}` item).
 */
function resolveOneOfBranch(
  branches: JsonSchema[],
  rootSchema: JsonSchema,
  data: unknown
): JsonSchema | undefined {
  const kind = (data as { kind?: unknown } | undefined)?.kind;
  if (typeof kind !== 'string') return undefined;
  return branches
    .map((branch) => dereference(branch, rootSchema))
    .find((branch) => (schemaProperties(branch).kind as { const?: unknown } | undefined)?.const === kind);
}

/**
 * A newly "+ Add"-ed view is a bare `{}` — no `kind`, so `resolveOneOfBranch`
 * can't match any `oneOf` branch and it would otherwise fall to
 * `RawJsonItemEditor` forever (a trainer would have to hand-type `"kind":
 * "array"` before any visual editor could take over). These are the minimal
 * valid skeleton for each kind, inserted by one click instead.
 */
const VIEW_KIND_DEFAULTS: Record<string, unknown> = {
  array: { kind: 'array', values: [1, 2, 3] },
  stack: { kind: 'stack', values: [1, 2, 3] },
  grid: { kind: 'grid', values: [[1, 2], [3, 4]] },
  tree: { kind: 'tree', rootId: 'root', nodes: [{ id: 'root', value: 1, children: [] }] },
  graph: { kind: 'graph', nodes: [{ id: 'a' }, { id: 'b' }], edges: [] },
  linkedlist: { kind: 'linkedlist', nodes: [{ id: 'a', value: 1 }] },
};

const VIEW_KIND_LABELS: Record<string, string> = {
  array: 'Array',
  stack: 'Stack',
  grid: 'Grid',
  tree: 'Tree',
  graph: 'Graph',
  linkedlist: 'Linked List',
};

/** Shown instead of the raw-JSON fallback for a freshly-added, kind-less view — one click starts the right visual editor with sensible sample data. */
function ViewKindPicker({ onPick }: { onPick: (kind: string) => void }) {
  return (
    <div>
      <p className="mb-2 text-xs text-[#374151]">What should this view show?</p>
      <div className="flex flex-wrap gap-2">
        {Object.keys(VIEW_KIND_DEFAULTS).map((kind) => (
          <button
            key={kind}
            type="button"
            onClick={() => onPick(kind)}
            className="rounded-md border border-[#d1d5db] px-3 py-1.5 text-xs font-medium text-[#374151] hover:border-primary hover:text-primary"
          >
            {VIEW_KIND_LABELS[kind]}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Bridges `FocusStepContext` (only reachable here, inside the JSON Forms tree) into a plain `onFocusStep` prop `VisualizationStageEditor` can call like any other callback. */
function StepArrayWithFocusReporting({
  variantIndex,
  steps,
  onChange,
}: {
  variantIndex: number;
  steps: Step[];
  onChange: (next: Step[]) => void;
}) {
  const reportFocusStep = useContext(FocusStepContext);
  return (
    <VisualizationStageEditor
      steps={steps}
      onChange={onChange}
      onFocusStep={(stepIndex) => reportFocusStep(variantIndex, stepIndex)}
    />
  );
}

/** Array of objects (variants, steps, nodes, edges, marks, …), always expanded — no accordions. */
function ObjectArrayControl(props: ControlProps) {
  const items: unknown[] = Array.isArray(props.data) ? props.data : [];
  const childSchema: JsonSchema = dereference(
    (props.schema.items as JsonSchema) ?? {},
    props.rootSchema
  );

  // `Step.views` resolves to the discriminated `View` oneOf. Every branch
  // except `linkedlist` now has a dedicated visual editor — `tree`/`graph`
  // route to the React Flow canvas (Task 8), `array`/`stack` to
  // `VisualizationArrayGraphCanvas`, `grid` to `VisualizationGridCanvas`.
  // `linkedlist` is the one remaining raw-JSON fallback, resolved per-item
  // since a single `views` array can legitimately mix kinds (e.g. a heap
  // step showing both a tree and its array form).
  if (childSchema.oneOf) {
    const branches = childSchema.oneOf as JsonSchema[];
    if (!props.visible) return null;
    return (
      <div className="mb-3 rounded-md border border-[#e5e7eb] p-2">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs font-medium text-[#374151]">{fieldLabel(props)}</span>
          <button
            type="button"
            className="text-xs text-primary hover:underline"
            onClick={() => props.handleChange(props.path, [...items, {}])}
          >
            + Add
          </button>
        </div>
        {items.map((item, index) => {
          const itemPath = `${props.path}.${index}`;
          const branch = resolveOneOfBranch(branches, props.rootSchema, item);
          const branchKind = (schemaProperties(branch ?? {}).kind as { const?: unknown } | undefined)
            ?.const;
          return (
            <div key={index} className="mb-2 rounded-md border border-[#f0f0f0] bg-[#fafafa]/60 p-2">
              <div className="mb-1 flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-wide text-[#9ca3af]">
                  #{index + 1}
                </span>
                <button
                  type="button"
                  className="text-xs text-destructive hover:underline"
                  onClick={() => props.handleChange(props.path, items.filter((_, i) => i !== index))}
                >
                  Remove
                </button>
              </div>
              {branchKind === 'tree' || branchKind === 'graph' ? (
                <VisualizationTreeGraphCanvas
                  kind={branchKind as 'tree' | 'graph'}
                  value={item as TreeView | GraphView}
                  onChange={(next) => props.handleChange(itemPath, next)}
                />
              ) : branchKind === 'array' || branchKind === 'stack' ? (
                <VisualizationArrayGraphCanvas
                  kind={branchKind as 'array' | 'stack'}
                  value={item as ArrayView | StackView}
                  onChange={(next) => props.handleChange(itemPath, next)}
                />
              ) : branchKind === 'grid' ? (
                <VisualizationGridCanvas
                  value={item as GridView}
                  onChange={(next) => props.handleChange(itemPath, next)}
                />
              ) : branchKind === undefined && Object.keys(item as object).length === 0 ? (
                <ViewKindPicker
                  onPick={(kind) => props.handleChange(itemPath, VIEW_KIND_DEFAULTS[kind])}
                />
              ) : (
                <RawJsonItemEditor
                  label={typeof (item as { kind?: unknown })?.kind === 'string' ? String((item as { kind: string }).kind) : 'view'}
                  value={item}
                  onChange={(next) => props.handleChange(itemPath, next)}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  if (!props.visible) return null;

  // A `Step` (narration + state + views) is the one object-array item shape
  // that gets a fully custom editor instead of JSON Forms' generic per-field
  // dispatch: `VisualizationStageEditor` renders the whole `steps` array as
  // one persistent stage + filmstrip (narration, state, and views all have
  // dedicated hand-built editors of their own now, so there's nothing left
  // for a generic field-by-field box list to usefully add). Detected by its
  // distinctive property set since JSON Forms doesn't guarantee path/label
  // stays stable.
  const itemSchemaKeys = new Set(Object.keys(schemaProperties(childSchema)));
  const isStepArray =
    itemSchemaKeys.has('narration') && itemSchemaKeys.has('views') && itemSchemaKeys.has('state');
  if (isStepArray) {
    // `props.path` for this control is e.g. "script.variants.0.steps" — the
    // variant index is embedded in it since JSON Forms scopes each control
    // instance to the exact field it renders.
    const variantMatch = /variants[./](\d+)[./]steps$/.exec(props.path);
    const variantIndex = variantMatch ? Number(variantMatch[1]) : 0;
    return (
      <StepArrayWithFocusReporting
        variantIndex={variantIndex}
        steps={items as Step[]}
        onChange={(next) => props.handleChange(props.path, next)}
      />
    );
  }

  // `Variant.complexity_time`/`complexity_space` get the same "Advanced"
  // collapse as the root's bookkeeping fields (see `ADVANCED_VARIANT_KEYS`).
  const isVariantArray = ADVANCED_VARIANT_KEYS.every((key) => itemSchemaKeys.has(key));
  const itemUischema = isVariantArray
    ? buildGroupedUischema(childSchema, ADVANCED_VARIANT_KEYS)
    : buildVerticalUischema(childSchema);

  return (
    <div className="mb-3 rounded-md border border-[#e5e7eb] p-2">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-medium text-[#374151]">{fieldLabel(props)}</span>
        <button
          type="button"
          className="text-xs text-primary hover:underline"
          onClick={() => props.handleChange(props.path, [...items, {}])}
        >
          + Add
        </button>
      </div>
      {items.map((_, index) => (
        <div key={index} className="mb-2 rounded-md border border-[#f0f0f0] bg-[#fafafa]/60 p-2">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-wide text-[#9ca3af]">
              #{index + 1}
            </span>
            <button
              type="button"
              className="text-xs text-destructive hover:underline"
              onClick={() => props.handleChange(props.path, items.filter((_, i) => i !== index))}
            >
              Remove
            </button>
          </div>
          <JsonFormsDispatch
            schema={childSchema}
            uischema={itemUischema as never}
            path={`${props.path}.${index}`}
            renderers={props.renderers}
            cells={props.cells}
          />
        </div>
      ))}
    </div>
  );
}

/**
 * Fallback for the one shape this base editor still doesn't render
 * specifically: `Step.views` items whose discriminated `oneOf` branch has no
 * dedicated visual canvas (currently only `linkedlist`; `array`/`grid`/
 * `stack`/`tree`/`graph` all route elsewhere — see `ObjectArrayControl`).
 * Mirrors the existing controls' UX — a labeled textarea, JSON-parsed on
 * change — rather than a fully authored editor, since linked lists are rare
 * enough in this content that a dedicated canvas isn't yet justified.
 *
 * `RawJsonItemEditor` is one instance per array INDEX — `ObjectArrayControl`
 * keys `Step.views` items by index, not a stable id (there is no stable id
 * anywhere in this schema). When an earlier sibling View is removed, React
 * reconciles the surviving component instance at that index and reuses it
 * for a DIFFERENT logical item. Local `text` state does not reset on its own
 * in that case — without the resync effect below it would keep showing the
 * previous occupant's stale JSON, and a subsequent edit would silently
 * overwrite the new item's data at that index. `useRawJsonEditorState` is
 * where this resync logic lives.
 *
 * `lastEmittedSerialized` records the serialized form THIS instance last
 * produced via its own `onChange`, so the effect can tell apart:
 *  - `value` changed because our own edit round-tripped back through
 *    `onChange` (serialized form matches what we just emitted — skip
 *    resync, so we don't fight the user's in-progress formatting/whitespace
 *    while they're still typing), vs.
 *  - `value` changed because a sibling was removed/reordered and this index
 *    now holds a genuinely different item (serialized form does NOT match —
 *    resync `text` to the new item's real content).
 */
function useRawJsonEditorState(value: unknown, isArraySchema: boolean) {
  const [text, setText] = useState(() => JSON.stringify(value ?? (isArraySchema ? [] : {}), null, 2));
  const [invalid, setInvalid] = useState(false);
  const lastEmittedSerialized = useRef<string | null>(null);

  useEffect(() => {
    const serialized = JSON.stringify(value ?? (isArraySchema ? [] : {}), null, 2);
    if (serialized === lastEmittedSerialized.current) return;
    setText(serialized);
    setInvalid(false);
  }, [value, isArraySchema]);

  const handleTextChange = (raw: string, onChange: (next: unknown) => void) => {
    setText(raw);
    try {
      const parsed = JSON.parse(raw);
      setInvalid(false);
      lastEmittedSerialized.current = JSON.stringify(parsed, null, 2);
      onChange(parsed);
    } catch {
      setInvalid(true);
    }
  };

  return { text, invalid, handleTextChange };
}

/** Shared textarea UI for both raw-JSON fallbacks — label + caption + textarea + error. */
function RawJsonEditorBody({
  value,
  onChange,
  label,
  isArraySchema = false,
}: {
  value: unknown;
  onChange: (next: unknown) => void;
  label: string;
  isArraySchema?: boolean;
}) {
  const { text, invalid, handleTextChange } = useRawJsonEditorState(value, isArraySchema);
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-medium text-[#374151]">{label}</span>
        <span className="text-[11px] text-[#9ca3af]">raw JSON — editor coming soon</span>
      </div>
      <textarea
        className="w-full rounded-md border border-[#d1d5db] px-2 py-1 font-mono text-xs"
        rows={6}
        value={text}
        onChange={(e) => handleTextChange(e.target.value, onChange)}
      />
      {invalid && <p className="mt-1 text-[11px] text-red-600">Invalid JSON</p>}
    </div>
  );
}

/**
 * Bound to a single array item's value/onChange rather than a whole
 * `ControlProps` object — used for `View` items whose `oneOf` branch this
 * editor has no dedicated renderer for (array/grid/linkedlist/stack; see
 * `ObjectArrayControl`'s `oneOf` handling for `tree`/`graph`, which route to
 * `VisualizationTreeGraphCanvas` instead).
 */
function RawJsonItemEditor({
  value,
  onChange,
  label,
}: {
  value: unknown;
  onChange: (next: unknown) => void;
  label: string;
}) {
  return <RawJsonEditorBody value={value} onChange={onChange} label={label} />;
}

function ObjectControl(props: ControlProps) {
  if (!props.visible) return null;
  const properties = schemaProperties(props.schema);

  // Dictionary-shaped schemas (`additionalProperties`, no `properties` key)
  // have nothing for this control to iterate over field-by-field. The only
  // one in this schema is `Step.state: { [key: string]: StateValue }` (see
  // `StateKeyValueEditor`'s doc comment), so it's safe to route every case
  // of this shape straight to the dedicated key/value row editor rather than
  // a raw-JSON textarea.
  if (Object.keys(properties).length === 0 && props.schema.additionalProperties) {
    return (
      <div className="mb-3 rounded-md border border-[#e5e7eb] p-2">
        <StateKeyValueEditor
          value={props.data}
          onChange={(next) => props.handleChange(props.path, next)}
        />
      </div>
    );
  }

  return (
    <div className="mb-3 rounded-md border border-[#e5e7eb] p-2">
      {props.label && <p className="mb-1 text-xs font-semibold text-[#374151]">{props.label}</p>}
      {Object.keys(properties).map((key) => (
        <JsonFormsDispatch
          key={key}
          schema={props.schema}
          uischema={{ type: 'Control', scope: `#/properties/${key}` } as never}
          path={props.path}
          renderers={props.renderers}
          cells={props.cells}
        />
      ))}
    </div>
  );
}

function VerticalLayoutRenderer(props: LayoutProps) {
  if (!props.visible) return null;
  const layout = props.uischema as Layout;
  return (
    <div>
      {layout.elements.map((element, index) => (
        <JsonFormsDispatch
          key={index}
          schema={props.schema}
          uischema={element}
          path={props.path}
          renderers={props.renderers}
          cells={props.cells}
        />
      ))}
    </div>
  );
}

/**
 * Renders a `{ type: 'Group', label, elements }` uischema node — used only
 * for the root's "Advanced" fields (`difficulty`/`leetcode_id`/
 * `problem_slug`/`schema_version`; see `buildRootUischema`). Collapsed by
 * default: a trainer building a visualization for the first time needs
 * Kind and the Variants/Steps content, not internal bookkeeping like
 * `schema_version`, which already defaults to `1` and is rarely touched.
 */
function AdvancedGroupRenderer(props: LayoutProps) {
  const [expanded, setExpanded] = useState(false);
  if (!props.visible) return null;
  const layout = props.uischema as Layout & { label?: string };
  return (
    <div className="mb-3">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="text-xs font-medium text-[#6b7280] hover:text-[#374151]"
      >
        {expanded ? '▾' : '▸'} {layout.label ?? 'Advanced'}
      </button>
      {expanded && (
        <div className="mt-2">
          {layout.elements.map((element, index) => (
            <JsonFormsDispatch
              key={index}
              schema={props.schema}
              uischema={element}
              path={props.path}
              renderers={props.renderers}
              cells={props.cells}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const ConnectedStringControl = withJsonFormsControlProps(StringControl);
const ConnectedNumberControl = withJsonFormsControlProps(NumberControl);
const ConnectedBooleanControl = withJsonFormsControlProps(BooleanControl);
const ConnectedEnumControl = withJsonFormsControlProps(EnumControl);
const ConnectedObjectControl = withJsonFormsControlProps(ObjectControl);
const ConnectedPrimitiveArrayControl = withJsonFormsControlProps(PrimitiveArrayControl);
const ConnectedObjectArrayControl = withJsonFormsControlProps(ObjectArrayControl);
const ConnectedVerticalLayout = withJsonFormsLayoutProps(VerticalLayoutRenderer);
const ConnectedAdvancedGroup = withJsonFormsLayoutProps(AdvancedGroupRenderer);

/**
 * JSON Forms' built-in `isObjectArrayControl` only matches an array control
 * whose item schema resolves (via `$ref`) to something with a top-level
 * `"type": "object"`. The generated `View` union (`Step.views`'s item type)
 * has no top-level `type` — it's a bare `oneOf` (plus the informal
 * `discriminator` block `ts-json-schema-generator` emits, which per Task 6 is
 * NOT load-bearing here) — so the built-in tester never matches it, and
 * without this tester the control would silently fall through to the
 * generic string-control fallback (rendering `views` as a single broken text
 * input) rather than reaching `ObjectArrayControl`'s own oneOf-branch
 * resolution (`resolveOneOfBranch`).
 */
const isOneOfItemsArrayControl = and(
  isControl,
  schemaMatches((schema) => schema.type === 'array' && !Array.isArray(schema.items)),
  schemaSubPathMatches('items', (itemSchema, rootSchema) => {
    const resolved = dereference((itemSchema ?? {}) as JsonSchema, (rootSchema ?? {}) as JsonSchema);
    return Array.isArray((resolved as { oneOf?: unknown[] })?.oneOf);
  })
);

const tailwindRenderers: JsonFormsRendererRegistryEntry[] = [
  { tester: rankWith(1, uiTypeIs('VerticalLayout')), renderer: ConnectedVerticalLayout },
  { tester: rankWith(1, uiTypeIs('Group')), renderer: ConnectedAdvancedGroup },
  { tester: rankWith(3, isBooleanControl), renderer: ConnectedBooleanControl },
  { tester: rankWith(3, isEnumControl), renderer: ConnectedEnumControl },
  { tester: rankWith(2, or(isNumberControl, isIntegerControl)), renderer: ConnectedNumberControl },
  { tester: rankWith(2, isStringControl), renderer: ConnectedStringControl },
  { tester: rankWith(4, isOneOfItemsArrayControl), renderer: ConnectedObjectArrayControl },
  { tester: rankWith(3, isObjectArrayControl), renderer: ConnectedObjectArrayControl },
  { tester: rankWith(3, isPrimitiveArrayControl), renderer: ConnectedPrimitiveArrayControl },
  {
    tester: rankWith(2, and(isControl, isObjectControl)),
    renderer: ConnectedObjectControl,
  },
  {
    // Fallback for any schema-typed control JSON Forms couldn't classify above.
    tester: rankWith(1, and(isControl, schemaMatches(() => true))),
    renderer: ConnectedStringControl,
  },
];

/**
 * A partially-authored draft is expected the whole time someone is building a
 * visualization from scratch — clicking "+ Add" under Variants/Steps pushes a
 * bare `{}` (see `ObjectArrayControl`), which has no `steps`/`views` array at
 * all until the author fills those fields in. `buildTimeline` (and the
 * renderers it feeds, e.g. `ArrayRenderer` reading `view.values.length`)
 * assume a fully-shaped `StepScript` and throw on `undefined.forEach`/
 * `undefined.length` otherwise. This guard is the single gate deciding
 * whether the draft is complete enough to attempt a preview at all — nothing
 * downstream of it should call `buildTimeline` on an unguarded script.
 */
/**
 * Checks every field the generated schema's `required` arrays list for
 * `Variant` (`label`, `pseudocode`, `steps`) and `Step` (`narration`,
 * `views`) — not just the array fields `buildTimeline` itself walks. A
 * variant/step can satisfy the narrower "has array `steps`/`views`" check
 * and still be missing `pseudocode` (also required) the moment it's added
 * via "+ Add" and before the author fills in any pseudocode lines —
 * `PseudocodePanel` reads `lines.length`/`lines.map` on it unconditionally,
 * so an under-checked guard here still lets the crash through to
 * `PreviewErrorBoundary`, just one field later than the original bug. That's
 * not a route crash, but it swaps the intended "add at least one variant
 * with a step" empty-state message for a generic "couldn't render" error on
 * the very first-time-authoring flow this guard exists to keep smooth.
 */
function isWellFormedScript(script: StepScript | undefined): script is StepScript {
  if (!script || !Array.isArray(script.variants) || script.variants.length === 0) return false;
  return script.variants.every((variant) => isWellFormedVariant(variant));
}

function isWellFormedVariant(variant: unknown): variant is Variant {
  if (!variant || typeof variant !== 'object') return false;
  const v = variant as Variant;
  return (
    typeof v.label === 'string' &&
    Array.isArray(v.pseudocode) &&
    Array.isArray(v.steps) &&
    v.steps.every((step) => isWellFormedStep(step))
  );
}

function isWellFormedStep(step: unknown): step is Step {
  if (!step || typeof step !== 'object') return false;
  const s = step as Step;
  return typeof s.narration === 'string' && Array.isArray(s.views);
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
  /** Reset the caught-error state whenever this changes (e.g. the script draft changes). */
  resetKey?: unknown;
}
interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Defense-in-depth for the preview pane: `isWellFormedScript` above guards the
 * one crash this task set out to fix (an incomplete draft), but the renderers
 * downstream (`ViewRenderer` → `ArrayRenderer`/`GraphRenderer`/etc.) can still
 * be reached with a well-formed-shape-but-semantically-broken script — e.g. a
 * view authored through the raw-JSON fallback as `{"kind":"array"}` with no
 * `values` array, which `ArrayRenderer` reads `.length` off unguarded. Rather
 * than chase every such renderer-level assumption individually, this boundary
 * ensures any of them failing degrades to an inline message instead of taking
 * down the whole editor route (and the author's unsaved draft with it).
 */
class PreviewErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    // eslint-disable-next-line no-console
    console.error('Visualization preview failed to render', error);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

function draftFromVisualization(viz: TopicVisualization): Record<string, unknown> {
  return {
    kind: viz.kind,
    leetcode_id: viz.leetcode_id,
    difficulty: viz.difficulty,
    problem_slug: viz.problem_slug,
    schema_version: viz.schema_version,
    script: viz.script,
  };
}

export const VisualizationEditor = forwardRef<VisualizationEditorHandle, Props>(
  function VisualizationEditor({ batchId, topicId, hasSourceTopic, onRequestClose }, ref) {
  const [draft, setDraft] = useState<Record<string, unknown>>(emptyDraft);
  const [existing, setExisting] = useState<TopicVisualization | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const [confirmRevertOpen, setConfirmRevertOpen] = useState(false);
  const [focusStep, setFocusStep] = useState<{ variantIndex: number; stepIndex: number } | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const previewPanelRef = useRef<HTMLDivElement>(null);
  const { isFullscreen: previewIsFullscreen, toggle: togglePreviewFullscreen } = useFullscreen(previewPanelRef);
  const [previewZoom, setPreviewZoom] = useState(1);
  const PREVIEW_ZOOM_MIN = 0.5;
  const PREVIEW_ZOOM_MAX = 2;
  // Bails out to the SAME object when nothing actually changed — `StepArrayWithFocusReporting`
  // passes a fresh `onFocusStep` closure every render, which would otherwise report on every
  // render and re-render this component in a loop.
  const reportFocusStep = useCallback((variantIndex: number, stepIndex: number) => {
    setFocusStep((prev) =>
      prev && prev.variantIndex === variantIndex && prev.stepIndex === stepIndex
        ? prev
        : { variantIndex, stepIndex }
    );
  }, []);

  // Snapshot of the draft as last loaded/saved/reverted — compared against
  // the live draft to decide whether closing needs an unsaved-changes guard.
  //
  // Kept in state (not a ref) DELIBERATELY: `isDirty` below is a `useMemo`
  // keyed on its dependencies, and a ref mutation doesn't change identity or
  // trigger a re-render, so a memo can never "see" a ref update on its own —
  // it only recomputes when something IT depends on (`draft`, or this) is a
  // state value that changed. A previous version of this stored the
  // snapshot in a ref and `handleSave` never called `setDraft` (it only
  // updated the ref + `existing`), so after a successful save `draft`'s
  // identity was unchanged, the memo didn't recompute, and `isDirty` stayed
  // `true` — a spurious "Discard unsaved changes?" prompt on a document with
  // zero unsaved changes. Every call site that "resets what counts as
  // saved" (initial load, save, revert) now goes through
  // `applyLoadedVisualization` below, which updates both `draft` and this
  // snapshot together in the same tick.
  const [savedSnapshot, setSavedSnapshot] = useState<string>(() => JSON.stringify(emptyDraft));

  /** Single place that resets "what counts as saved" — used by the initial
   * load, a successful save, and a successful revert-to-template, so
   * `draft` and `savedSnapshot` can never drift out of sync with each
   * other (see the comment on `savedSnapshot` above). */
  const applyLoadedVisualization = (viz: TopicVisualization | null) => {
    setExisting(viz);
    const loaded = viz ? draftFromVisualization(viz) : emptyDraft;
    setDraft(loaded);
    setSavedSnapshot(JSON.stringify(loaded));
  };

  useEffect(() => {
    if (!hasSourceTopic) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    trainerContentApi
      .getVisualization(batchId, topicId)
      .then((res) => {
        if (cancelled) return;
        if (res.data) {
          applyLoadedVisualization(res.data);
        }
      })
      .catch(() => toast.error('Failed to load visualization'))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId, topicId, hasSourceTopic]);

  const isDirty = useMemo(
    () => JSON.stringify(draft) !== savedSnapshot,
    [draft, savedSnapshot]
  );

  useImperativeHandle(
    ref,
    () => ({
      requestClose: () => {
        if (isDirty) {
          setConfirmDiscardOpen(true);
        } else {
          onRequestClose?.();
        }
      },
    }),
    [isDirty, onRequestClose]
  );

  const uischema = useMemo(() => buildGroupedUischema(resolvedVizSchema, ADVANCED_ROOT_KEYS), []);

  const script = draft.script as StepScript | undefined;
  const scriptIsPreviewable = useMemo(() => isWellFormedScript(script), [script]);
  const timeline = useMemo(
    () => (scriptIsPreviewable ? buildTimeline(script as StepScript) : []),
    [script, scriptIsPreviewable]
  );
  const previewVisualization: PlayerVisualization | null = useMemo(() => {
    if (!script || !scriptIsPreviewable || timeline.length === 0) return null;
    return {
      kind: (draft.kind as PlayerVisualization['kind']) ?? 'concept',
      leetcode_id: draft.leetcode_id as number | undefined,
      difficulty: draft.difficulty as PlayerVisualization['difficulty'],
      problem_slug: draft.problem_slug as string | undefined,
      schema_version: (draft.schema_version as number) ?? 1,
      script,
    };
  }, [draft, script, scriptIsPreviewable, timeline.length]);

  if (!hasSourceTopic) {
    return (
      <div className="rounded-lg border border-[#ececec] bg-[#fafafa]/40 p-4 text-xs text-[#6b7280]">
        This topic was not forked from a curriculum template, so it cannot have a visualization.
      </div>
    );
  }

  const handleSave = async () => {
    setSaving(true);
    setErrors([]);
    try {
      const res = await trainerContentApi.upsertVisualization(
        batchId,
        topicId,
        draft as unknown as UpsertVisualizationRequest
      );
      applyLoadedVisualization(res.data);
      toast.success('Visualization saved');
    } catch (err) {
      const axiosMessage = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      const fallbackMessage = (err as { message?: string })?.message ?? 'Save failed';
      setErrors((axiosMessage ?? fallbackMessage).split('; '));
    } finally {
      setSaving(false);
    }
  };

  /**
   * Deleting a batch-specific override now means "revert to the curriculum
   * template" (Task 11's fork-on-write: the backend's delete already falls
   * back to the template row), not "remove the visualization entirely" —
   * only reachable here when `existing.is_customized` is true, which implies
   * a template exists (this branch requires `hasSourceTopic`). Re-fetches
   * afterward to load the template's content back into the draft.
   */
  const handleRevertToTemplate = async () => {
    setReverting(true);
    try {
      await trainerContentApi.deleteVisualization(batchId, topicId);
      const res = await trainerContentApi.getVisualization(batchId, topicId);
      applyLoadedVisualization(res.data);
      toast.success('Reverted to the curriculum template');
    } catch {
      toast.error('Failed to revert to the curriculum template');
    } finally {
      setReverting(false);
    }
  };

  return (
    <div className="flex h-full flex-col gap-3">
      <AlertDialog open={confirmDiscardOpen} onOpenChange={setConfirmDiscardOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved edits to this visualization. Closing now will discard them —
              this can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            {/* Radix's `Action` already closes the dialog on click (same
                mechanism as `Cancel`) — no need to also flip
                `confirmDiscardOpen` here ourselves. */}
            <AlertDialogAction onClick={() => onRequestClose?.()}>
              Discard and close
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmRevertOpen} onOpenChange={setConfirmRevertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revert to the curriculum template?</AlertDialogTitle>
            <AlertDialogDescription>
              This discards everything built specifically for this batch and goes back to
              showing the shared template's default visualization instead — this can't be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRevertToTemplate}>
              Revert to template
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {existing && (
        <div className="flex items-start gap-2 rounded-md border border-[#e5e7eb] bg-[#f9fafb] px-3 py-2 text-xs text-[#374151]">
          <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[#6b7280]" />
          {existing.is_customized ? (
            <span>
              This visualization is specific to this batch. Editing it won't affect other
              batches, even ones built from the same curriculum template.
            </span>
          ) : (
            <span>
              You're viewing the shared template's default visualization. Saving will create a
              version specific to this batch — other batches won't be affected.
            </span>
          )}
        </div>
      )}

      {loading ? (
        <p className="text-xs text-[#6b7280]">Loading…</p>
      ) : (
        <div className="relative flex min-h-0 flex-1 flex-col gap-3">
          {/*
            `min-h-0` is required for the editor column's own `overflow-y-auto`
            to actually take effect — without it, a flex item's default
            `min-height: auto` refuses to shrink below its content size, and
            the OUTER dialog body ends up doing all the scrolling as one unit
            instead. This chain only resolves because the full-screen
            `DialogContent` host gives it a definite height (`h-screen` flex
            column) — this component assumes an ancestor already provides
            that, same as it assumed a `overflow-y-auto` ancestor before.

            The editor gets the FULL width by default — a trainer building a
            multi-block visualization needs the room. The live preview used
            to permanently claim half the screen in an even split; it now
            lives in a panel that slides in on demand instead.
          */}
          <div className="min-w-0 flex-1 overflow-y-auto pr-1">
            <FocusStepContext.Provider value={reportFocusStep}>
              <JsonForms
                schema={resolvedVizSchema as never}
                uischema={uischema as never}
                data={draft}
                renderers={tailwindRenderers}
                cells={[]}
                onChange={({ data }) => setDraft(data)}
              />
            </FocusStepContext.Provider>
          </div>

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPreviewOpen((o) => !o)}
            className="absolute right-2 top-0 gap-1.5"
          >
            {previewOpen ? <PanelRightClose className="h-4 w-4" /> : <PanelRight className="h-4 w-4" />}
            Preview
          </Button>

          {previewOpen && (
            <div
              ref={previewPanelRef}
              className={`absolute inset-y-0 right-0 z-20 flex w-full flex-col overflow-auto border border-[#ececec] bg-white p-3 shadow-xl ${
                previewIsFullscreen ? 'max-w-none' : 'max-w-[640px] rounded-md'
              }`}
            >
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="text-xs font-semibold text-[#374151]">Live preview</span>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={togglePreviewFullscreen}
                    className="h-7 w-7 text-[#6b7280] hover:text-[#111827]"
                    aria-label={previewIsFullscreen ? 'Exit fullscreen' : 'View preview fullscreen'}
                    title={previewIsFullscreen ? 'Exit fullscreen' : 'View preview fullscreen'}
                  >
                    {previewIsFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setPreviewZoom((z) => Math.max(PREVIEW_ZOOM_MIN, Math.round((z - 0.1) * 10) / 10))}
                    disabled={previewZoom <= PREVIEW_ZOOM_MIN}
                    className="h-7 w-7 text-[#6b7280] hover:text-[#111827]"
                    aria-label="Zoom out preview"
                  >
                    <ZoomOut className="h-4 w-4" />
                  </Button>
                  <button
                    type="button"
                    onClick={() => setPreviewZoom(1)}
                    className="w-10 shrink-0 text-center text-[11px] text-[#6b7280] hover:text-[#111827]"
                    aria-label="Reset preview zoom"
                    title="Reset zoom"
                  >
                    {Math.round(previewZoom * 100)}%
                  </button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setPreviewZoom((z) => Math.min(PREVIEW_ZOOM_MAX, Math.round((z + 0.1) * 10) / 10))}
                    disabled={previewZoom >= PREVIEW_ZOOM_MAX}
                    className="h-7 w-7 text-[#6b7280] hover:text-[#111827]"
                    aria-label="Zoom in preview"
                  >
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setPreviewZoom(1)}
                    className="h-7 w-7 text-[#6b7280] hover:text-[#111827]"
                    aria-label="Reset preview zoom"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setPreviewOpen(false)}
                    className="h-7 w-7 text-[#6b7280] hover:text-[#111827]"
                    aria-label="Close preview"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div style={{ transform: `scale(${previewZoom})`, transformOrigin: 'top left', width: `${100 / previewZoom}%` }}>
                {previewVisualization ? (
                  <PreviewErrorBoundary
                    resetKey={JSON.stringify(previewVisualization.script)}
                    fallback={
                      <p className="text-xs text-red-600">
                        Couldn't render the preview — something in this script (likely a raw-JSON
                        edited view) is missing fields the renderer needs. Your draft is unaffected.
                      </p>
                    }
                  >
                    <Player
                      visualization={previewVisualization}
                      batchId={batchId}
                      topicId={topicId}
                      focusStep={focusStep}
                    />
                  </PreviewErrorBoundary>
                ) : (
                  <p className="text-xs text-[#6b7280]">
                    Add at least one variant with a step to see a preview.
                  </p>
                )}
              </div>
            </div>
          )}

          {errors.length > 0 && (
            <ul className="flex-shrink-0 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">
              {errors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          )}

          <div className="flex flex-shrink-0 gap-2">
            <Button type="button" size="sm" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save Visualization'}
            </Button>
            {existing?.is_customized && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setConfirmRevertOpen(true)}
                disabled={reverting}
                className="border-destructive/50 text-destructive hover:bg-destructive/10"
              >
                {reverting ? 'Reverting…' : 'Revert to template'}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
  }
);
